import * as React from "react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Modal } from "../../../ui/components/Modal";
import { TextInput } from "../../../ui/components/Form";
import * as formStyles from "../../../ui/components/Form/FormInput.style.css";
import { useI18n } from "../../lib/i18n";
import {
  ROADMAP_COMPLEXITIES,
  ROADMAP_ORIGINS,
  ROADMAP_PRIORITIES,
  ROADMAP_STATUSES,
  type RoadmapItem,
  type RoadmapItemPayload,
} from "../../lib/api/roadmap";
import * as s from "./RoadmapItemModal.css";

interface RoadmapItemModalProps {
  open: boolean;
  item: RoadmapItem | null;
  defaultStatus?: RoadmapItemPayload["status"];
  onClose: () => void;
  onSubmit: (payload: RoadmapItemPayload) => Promise<void>;
}

type FormValues = {
  name: string;
  description: string;
  status: RoadmapItemPayload["status"];
  priority: string;
  complexity: string;
  category: string;
  origin: string;
  smartSource: string;
};

function initialValues(item: RoadmapItem | null, defaultStatus: FormValues["status"]): FormValues {
  return {
    name: item?.name ?? "",
    description: item?.description ?? "",
    status: item?.status ?? defaultStatus,
    priority: item?.priority ?? "",
    complexity: item?.complexity ?? "",
    category: item?.category ?? "",
    origin: item?.origin ?? (item ? "" : "new"),
    smartSource: item?.smartSource ?? "",
  };
}

const orNull = <T extends string>(value: string): T | null => (value.trim() ? (value.trim() as T) : null);

export function RoadmapItemModal({
  open,
  item,
  defaultStatus = "planned",
  onClose,
  onSubmit,
}: RoadmapItemModalProps) {
  const { t } = useI18n();
  const [values, setValues] = React.useState<FormValues>(() => initialValues(item, defaultStatus));
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setValues(initialValues(item, defaultStatus));
      setError(null);
    }
  }, [open, item, defaultStatus]);

  const set = <K extends keyof FormValues>(key: K, value: FormValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!values.name.trim()) {
      setError(t("pro.system.roadmap.modal.nameRequired"));
      return;
    }
    setSaving(true);
    try {
      await onSubmit({
        name: values.name.trim(),
        description: orNull(values.description),
        status: values.status,
        priority: orNull(values.priority),
        complexity: orNull(values.complexity),
        category: orNull(values.category),
        origin: orNull(values.origin),
        smartSource: orNull(values.smartSource),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("pro.system.roadmap.modal.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const select = (
    key: "status" | "priority" | "complexity" | "origin",
    options: readonly string[],
    labelPrefix: string,
    allowNone: boolean,
  ) => (
    <div className={s.field}>
      <label className={formStyles.label} htmlFor={`roadmap-${key}`}>
        {t(`pro.system.roadmap.field.${key}`)}
      </label>
      <select
        id={`roadmap-${key}`}
        className={formStyles.input}
        value={values[key]}
        onChange={(e) => set(key, e.target.value as FormValues[typeof key])}
        disabled={saving}
      >
        {allowNone && <option value="">{t("pro.system.roadmap.field.none")}</option>}
        {options.map((o) => (
          <option key={o} value={o}>
            {t(`${labelPrefix}.${o}`)}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <Modal
      open={open}
      setOpen={(v) => {
        if (!v && !saving) onClose();
      }}
      minWidth="520px"
      maxWidth="640px"
      closeOnClickOff
    >
      <Modal.Form onSubmit={handleSubmit}>
        <Modal.Header>
          <Text level="h3" weight="bold">
            {item ? t("pro.system.roadmap.modal.editTitle") : t("pro.system.roadmap.modal.createTitle")}
          </Text>
        </Modal.Header>
        <Modal.Body>
          <div className={s.body}>
            {error && <Callout variant="error">{error}</Callout>}

            <TextInput
              label={t("pro.system.roadmap.field.name")}
              value={values.name}
              onChange={(e) => set("name", e.target.value)}
              disabled={saving}
              autoFocus
            />

            <div className={s.field}>
              <label className={formStyles.label} htmlFor="roadmap-description">
                {t("pro.system.roadmap.field.description")}
              </label>
              <textarea
                id="roadmap-description"
                className={`${formStyles.input} ${s.textarea}`}
                value={values.description}
                onChange={(e) => set("description", e.target.value)}
                disabled={saving}
                rows={4}
              />
            </div>

            <div className={s.row}>
              {select("status", ROADMAP_STATUSES, "pro.system.roadmap.status", false)}
              {select("priority", ROADMAP_PRIORITIES, "pro.system.roadmap.priority", true)}
              {select("complexity", ROADMAP_COMPLEXITIES, "pro.system.roadmap.complexity", true)}
            </div>

            <div className={s.row}>
              <TextInput
                label={t("pro.system.roadmap.field.category")}
                value={values.category}
                onChange={(e) => set("category", e.target.value)}
                disabled={saving}
              />
              {select("origin", ROADMAP_ORIGINS, "pro.system.roadmap.origin", true)}
              <TextInput
                label={t("pro.system.roadmap.field.smartSource")}
                value={values.smartSource}
                onChange={(e) => set("smartSource", e.target.value)}
                disabled={saving}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="outline" color="neutral" onClick={onClose} disabled={saving}>
            {t("pro.system.roadmap.modal.cancel")}
          </Button>
          <Button type="submit" color="primary" loading={saving}>
            {item ? t("pro.system.roadmap.modal.save") : t("pro.system.roadmap.modal.create")}
          </Button>
        </Modal.Footer>
      </Modal.Form>
    </Modal>
  );
}
